import React, { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { PageHeader } from '@/components/shared'
import { emailSettingsService } from '@/services/emailSettingsService'

const Settings = () => {
  const queryClient = useQueryClient()
  const [fromEmail, setFromEmail] = useState('')
  const [fromName, setFromName] = useState('')
  const [replyToEmail, setReplyToEmail] = useState('')
  const [saving, setSaving] = useState(false)

  const { data, isLoading } = useQuery('email-settings', emailSettingsService.getSettings, {
    onError: () => toast.error('Failed to fetch settings'),
  })

  useEffect(() => {
    const settings = data?.data?.settings
    if (settings) {
      setFromEmail(settings.from_email || '')
      setFromName(settings.from_name || '')
      setReplyToEmail(settings.reply_to_email || '')
    }
  }, [data])

  const handleSave = async () => {
    setSaving(true)
    try {
      await emailSettingsService.updateSettings({ from_email: fromEmail, from_name: fromName, reply_to_email: replyToEmail })
      toast.success('Settings saved')
      queryClient.invalidateQueries('email-settings')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-muted-foreground">Loading...</div>
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" description="Sender identity used for all outgoing campaigns" />

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="text-base">Sender Identity</CardTitle>
          <CardDescription>Shown as the "From" address on every campaign, unless overridden.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>From Name</Label>
            <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Buzzinga Team" />
          </div>
          <div className="space-y-1.5">
            <Label>From Email</Label>
            <Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="hello@yourdomain.com" />
          </div>
          <div className="space-y-1.5">
            <Label>Reply-To Email (optional)</Label>
            <Input value={replyToEmail} onChange={(e) => setReplyToEmail(e.target.value)} placeholder="support@yourdomain.com" />
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Settings
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export default Settings
